// Profit — "did the desk make money, and where did it go".
//
// A report, not a workflow: read-only, dense, no stray buttons. (If actions are
// ever wanted here they must come from components/ActionBar.tsx, which is THE
// canonical action row — never hand-rolled buttons. A P&L has none.)
//
// THE ONE RULE THIS SCREEN EXISTS TO MAKE VISIBLE
// (docs/CHART-OF-ACCOUNTS.md §3, operator-locked 2026-08-13):
//
//     profit = income − expenses
//
//   An expense reduces profit. A drawing does NOT — it is profit already
//   earned, taken out. So capital and drawings live in their own Owner block
//   BELOW the profit line and are never added into it. That confusion is the
//   exact bug this build exists to fix; the layout must keep it impossible.
//
// Currency: per-asset throughout. The BFF converts nothing and neither does
// this screen — a single blended number would be an invented FX rate.
//
// Layout:
//   lg+      one result strip — the profit per currency, first — over one matrix:
//            report lines down, currencies across (the console way)
//   below lg one block per currency, stacked — the same rows from the same
//            builder (reportRows), two columns wide, under a LedgerHead that
//            carries the currency and its profit, as every currency block on
//            Trades, Money and a statement does (19 Sept 2026, "fix the phone
//            layout for the profit screen too": the cards had their own row
//            shape — muted 14px labels beside 16px figures, uppercase group
//            words, a bare currency name — none of it the ReportTable's, and
//            the one figure the screen exists for sat at the bottom of each).
//
// 14 Sept 2026 (SWEEP-SCOPE §5.22): the period chips, table and figure are
// the shared pieces (PeriodChips, ReportTable); the per-currency note and the
// drawing sentence — pushed as a box on every load and under every phone card
// — live in "About this screen"; a warning is one sentence (fact +
// consequence) with its mechanism in About; "Spread on trades" reads
// "Margin"; the Owner block says "not part of profit" in its heading.
//
// 21 Oct 2026 audit (operator, overnight: "I need clean view for operator"):
//   · the answer first on the console too — the strip above the matrix reads the
//     profit per currency from the very figures the phone's block heads read
//     (data.profit), computing nothing of its own;
//   · profit that the report itself says is INCOMPLETE (margin not posting, a
//     partial scan) is no longer drawn as a bold green or red result under a
//     warning that says it is wrong: neutral, with the word beside it;
//   · the warnings are ONE line, not a stack of three plus a tag — every fact
//     kept (what stops a wrong click is shortened and joined, never deleted);
//   · a phone block says its profit once (the head, not the head and the last
//     row), and a section of one line does not repeat itself as its own total;
//   · "both desks" is gone: IndiaOTC is closed (operator, 18 Oct 2026), the
//     desk is one.
//
// 21 Oct 2026 design-system sweep: the one warning line had become a 45-word amber
// paragraph (every warning's sentence joined to the next). It is now the NAMES of the
// warnings — "Margin missing · No opening cost · Trade log unread", three at most, then
// "+N" — as a StateLine, each warning's sentence behind "Details" and a link to
// Inventory where a cost cures it. The qualifier beside the figure (incomplete / not
// checked / estimate) is unchanged. A failed read keeps the header and its period chips,
// says what failed in a line of its own with "Try again", and is never an empty report.

import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { readWords } from '../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { PeriodChips, reportLabel, reportPeriod, type PeriodValue } from '../components/PeriodChips';
import { ReportTable, type ReportRow, Figure } from '../components/ReportTable';
import { LedgerHead } from '../components/Ledger';
import { EmptyState, Pending, StateLine } from '../components/kit';
import type { PnlAmounts, PnlLine, PnlNote, PnlResponse, PnlSection } from '../lib/api';
import { displayAsset, fmtAmount } from '../lib/format';

/** Assets worth a column: anything the report actually has a number for. */
function usedAssets(data: PnlResponse): string[] {
  const used = new Set<string>();
  const eat = (bag: PnlAmounts) => { for (const [asset, v] of Object.entries(bag)) if (v !== 0) used.add(asset); };
  for (const s of [data.income, data.expenses, data.owner.capital_in, data.owner.drawings, data.owner.other]) {
    eat(s.totals);
    for (const l of s.lines) eat(l.amounts);
  }
  eat(data.profit);
  return data.assets.filter((a) => used.has(a));
}

function isEmpty(data: PnlResponse): boolean {
  return data.income.lines.length === 0
    && data.expenses.lines.length === 0
    && data.owner.capital_in.lines.length === 0
    && data.owner.drawings.lines.length === 0
    && data.owner.other.lines.length === 0;
}

/** The one wording for a report line (§6 "Margin"): the ledger's "Spread on
 *  trades" reads "Margin" on screen. Keyed on the line's stable id, so the
 *  BFF's label is never matched by its words. */
function lineLabel(l: PnlLine): string {
  return l.key === 'spread' ? 'Margin' : l.label;
}

/** WHEN THE PROFIT IS NOT A RESULT (21 Oct 2026 audit). Three warnings say, in
 *  their own words, that part of the profit is missing: the margin is not
 *  posting ("profit is understated"), the scan was cut short ("numbers are
 *  partial"), accounts were clipped ("some categories may be missing"). The
 *  figure under such a warning was still drawn as a bold green or red result
 *  — a headline that contradicted the line above it. It is neutral now, and
 *  says INCOMPLETE beside it. The other warnings (an assumed cost, a trade log
 *  that did not answer, no opening cost) qualify the profit and keep their
 *  line, but do not say a part is missing, so they leave the figure as it
 *  was. Keyed on the note's code, never on its words. */
const INCOMPLETE_CODES = new Set(['spread_not_posting', 'spread_mismatch', 'scan_truncated', 'accounts_clipped']);

/** THE WORD A QUALIFIED PROFIT WEARS (Astra review, 21 Oct 2026: a profit under
 *  "the trade log did not answer", or on an assumed or missing cost, was still
 *  drawn as a green result). A figure is never a clean result while its own page
 *  says it is partial, unchecked or an estimate — one word beside it, neutral
 *  colour. Priority: part missing > unchecked > estimate. Keyed on the code. */
const QUALIFIERS: ReadonlyArray<readonly [string, ReadonlySet<string>]> = [
  ['incomplete',  INCOMPLETE_CODES],
  ['not checked', new Set(['trade_log_unavailable'])],
  ['estimate',    new Set(['inventory_basis_missing', 'margin_uses_assumed_cost'])],
];
function qualifierOf(warnings: PnlNote[]): string | null {
  for (const [word, codes] of QUALIFIERS) if (warnings.some((n) => codes.has(n.code))) return word;
  return null;
}

/** What the report knows about itself that changes how a row is drawn. */
interface View {
  /** The profit is partial, unchecked or an estimate — draw it neutral, with the word. */
  incomplete: boolean;
  /** …and the word: incomplete · not checked · estimate. */
  word: string;
  /** The "margin reads zero" warning is on screen — the Margin line's own
   *  "not posting yet" tag would be the same fact twice. */
  spreadSaid: boolean;
}

/** The profit figure wherever it stands (the strip, the matrix's Profit row, a
 *  phone block's head): the sign's colour as everywhere on this screen — and
 *  none at all when the profit is incomplete. Same faces as the shared
 *  Figure's, so nothing moves when the profit is whole. `className` is the
 *  seat's face: `amt` in a head, `amt-key …` where the figure is the answer. */
function ProfitFigure({ value, asset, incomplete, className = 'amt' }: {
  value: number | null | undefined; asset: string; incomplete: boolean; className?: string;
}) {
  if (value === null || value === undefined) return <span className="text-tg-hint">—</span>;
  const colour = incomplete ? '' : value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : '';
  return <span className={`${className} ${colour}`}>{fmtAmount(value, asset)}</span>;
}

function firstSentence(message: string): string {
  return message.split(/(?<=\.)\s+/)[0] ?? message;
}

/** A WARNING IS ONE SENTENCE — the fact and its consequence. The BFF's note
 *  carries the mechanism too ("Margin is the sale rate less the average cost
 *  of the inventory sold, so it cannot be worked out until…"); that half is
 *  in About this screen, once, not in a box above the numbers. Keyed on the
 *  note's code; an unknown code keeps its first sentence. */
function shortWarning(n: PnlNote, data: PnlResponse): string {
  const first = firstSentence(n.message);
  switch (n.code) {
    case 'spread_not_posting': {
      const t = data.meta.trades_in_period;
      const booked = t !== null && t > 0 ? `${t} ${t === 1 ? 'trade' : 'trades'} booked, ` : '';
      return `Margin reads zero — ${booked}no margin reached the ledger; profit is understated.`;
    }
    case 'inventory_basis_missing':
      return 'No opening cost on the inventory held — a sale reads as all profit; set it on Inventory.';
    case 'margin_uses_assumed_cost': {
      const which = /:\s*(.+?)\.\s*Treat/.exec(n.message)?.[1];
      return `Margin on ${which ?? 'some books'} is measured against an assumed cost — an estimate until it is confirmed.`;
    }
    case 'trade_log_unavailable':
      return 'The trade log did not answer — income here is unchecked against the deals behind it.';
    case 'accounts_clipped':
      return 'More accounts than this report reads at once — some categories may be missing.';
    case 'scan_truncated':
      return `${first} Numbers are partial — narrow the period.`;
    case 'spread_mismatch':
      return first;
    default:
      return first;
  }
}

/** THE WARNINGS, AS ONE LINE (21 Oct 2026 sweep: they were one 45-word amber
 *  paragraph — each warning's sentence joined to the next). The line names each
 *  warning in at most three words; the sentence — fact and consequence, from
 *  `shortWarning` — is one tap down, for EVERY warning, so nothing that stops a
 *  wrong click is lost, only the wall. Keyed on the note's code, never on its
 *  words. A code with no name here shows its first sentence whole in the line
 *  (its rest behind the tap): a new warning is never swallowed for want of a label. */
const WARNING_NAME: Readonly<Record<string, string>> = {
  spread_not_posting:       'Margin missing',
  spread_mismatch:          'Margin gap',
  inventory_basis_missing:  'No opening cost',
  margin_uses_assumed_cost: 'Assumed cost',
  trade_log_unavailable:    'Trade log unread',
  accounts_clipped:         'Accounts clipped',
  scan_truncated:           'Partial scan',
};
/** The warnings an opening cost cures — the margin cannot be worked out until each inventory
 *  book has one, an assumed one is an estimate until it is confirmed: the line links to Inventory. */
const SET_COST_CODES: ReadonlySet<string> = new Set(['inventory_basis_missing', 'margin_uses_assumed_cost', 'spread_not_posting']);
/** How many names the line carries before "+N". */
const NAMES_SHOWN = 3;

/** The line: up to three names, then "+N" for the rest. A name a screen has is a
 *  nowrap piece (a label never breaks in the middle on a phone); an unnamed code's
 *  sentence wraps as a sentence does. */
function WarningNames({ warnings }: { warnings: PnlNote[] }) {
  const shown = warnings.slice(0, NAMES_SHOWN);
  const more = warnings.length - shown.length;
  const pieces: Array<{ key: string; text: string; named: boolean }> = [
    ...shown.map((n) => ({ key: n.code, text: WARNING_NAME[n.code] ?? firstSentence(n.message), named: n.code in WARNING_NAME })),
    ...(more > 0 ? [{ key: 'more', text: `+${more}`, named: true }] : []),
  ];
  return (
    <>
      {pieces.map((p, i) => (
        <Fragment key={p.key}>
          <span className={p.named ? 'whitespace-nowrap' : ''}>{p.text}{i < pieces.length - 1 ? ' ·' : ''}</span>
          {i < pieces.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </>
  );
}

/** What is behind "Details": each warning's one sentence. An unnamed code has its first
 *  sentence in the line when it is among the first names, so only the rest is here. */
function warningDetails(warnings: PnlNote[], data: PnlResponse): ReactNode[] {
  return warnings.flatMap((n, i) => {
    if (n.code in WARNING_NAME) return [<p key={n.code}>{shortWarning(n, data)}</p>];
    const rest = i < NAMES_SHOWN ? n.message.slice(firstSentence(n.message).length).trim() : n.message;
    return rest ? [<p key={n.code}>{rest}</p>] : [];
  });
}

export function Profit() {
  const [period, setPeriod] = useState<PeriodValue>({ key: 'month' });
  // Named periods are the BFF's own Dubai windows; only "Dates…" sends days.
  const { key: asked, ...days } = reportPeriod(period);   // Yesterday goes as its two days

  const q = useQuery({
    queryKey: ['pnl', asked, days.from ?? null, days.to ?? null],
    queryFn: () => api.pnl({ period: asked, ...days }),
  });

  const data = q.data;
  const info = data ? data.notes.filter((n) => n.tone !== 'warn') : [];

  // The header stands in every state — a read that is slow or failed still lets the period be changed.
  const header = (
    <ScreenHeader
      title="Profit"
      subtitle={reportLabel(period, data?.period.label)}
      help={
        <>
          <p>Profit is income less expenses, in each currency on its own. Nothing is converted, so there is no single profit number.</p>
          {info.filter((n) => n.code !== 'per_currency').map((n) => <p key={n.code}>{n.message}</p>)}
          <p>
            Margin is the sale rate less the average cost of the inventory sold, so it can only be
            measured once each inventory book has a cost. Until then it reads zero here and profit is
            understated by that amount. Margin measured against an assumed opening cost is an estimate
            until the cost is confirmed.
          </p>
          <p>
            Owner money — capital put in, drawings taken out — sits under the profit line and is never
            added into it. A drawing is profit already earned, taken out.
          </p>
        </>
      }
    >
      <PeriodChips value={period} onChange={setPeriod} allTime />
    </ScreenHeader>
  );

  // A FAILED READ is its own line — never the empty report's look: the first sentence of what the
  // books said, the rest behind a tap, and the one way out.
  if (q.isError) {
    const said = readWords(q.error);
    const first = firstSentence(said);
    const rest = said.slice(first.length).trim();
    return (
      <div className="space-y-4">
        {header}
        <StateLine
          tone="bad"
          more={rest || undefined}
          action={<button type="button" className="btn-link" onClick={() => q.refetch()}>Try again</button>}
        >
          Couldn&rsquo;t read profit. {first}
        </StateLine>
      </div>
    );
  }
  if (!data) return <div className="space-y-4">{header}<Pending /></div>;

  const assets = usedAssets(data);
  // No figure anywhere means the plain sentence, not a table of dashes.
  const nothingYet = isEmpty(data) || assets.length === 0;
  const warnings = data.notes.filter((n) => n.tone === 'warn');
  const details = warningDetails(warnings, data);
  const view: View = {
    incomplete: qualifierOf(warnings) !== null,
    word: qualifierOf(warnings) ?? 'incomplete',
    spreadSaid: warnings.some((n) => n.code === 'spread_not_posting'),
  };

  return (
    <div className="space-y-4">
      {header}

      {/* ONE warning line (21 Oct 2026 audit: three amber lines and a tag said one state four
          times; sweep: and then one 45-word paragraph). The names of the warnings, three at most
          and "+N"; each warning's sentence is behind "Details" — every fact reachable. The link
          goes where a cost is set, for the warnings that a cost cures. */}
      {warnings.length > 0 && (
        <div data-profit-warnings={warnings.length}>
          <StateLine
            tone="warn"
            more={details.length > 0 ? details : undefined}
            action={warnings.some((n) => SET_COST_CODES.has(n.code)) ? <Link to="/inventory" className="btn-link">Inventory →</Link> : undefined}
          >
            <WarningNames warnings={warnings} />
          </StateLine>
        </div>
      )}

      {nothingYet ? (
        <EmptyState>Nothing in this period.</EmptyState>
      ) : (
        <>
          {/* ── lg+: one matrix, currencies across ───────────────────────── */}
          <div className="hidden lg:block space-y-3">
            <ResultStrip data={data} assets={assets} view={view} />
            <Matrix data={data} assets={assets} view={view} />
          </div>

          {/* ── below lg: one block per currency ─────────────────────────── */}
          <div className="lg:hidden">
            {assets.map((asset) => <CurrencyBlock key={asset} data={data} asset={asset} view={view} />)}
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════ The rows — one builder for both forms ════════════════ */

/** The report's rows for a set of currencies: the desktop matrix asks for
 *  every currency in use, a phone block for one. ONE builder, so the two
 *  forms can never disagree on a line, a word, a weight or a figure.
 *
 *  `every` — the matrix — keeps every line and every total the BFF sent,
 *  a dash where a currency has no figure: a category with history and no
 *  movement this period stays visible across the currencies rather than
 *  absent (the BFF keeps it for that reason). A phone block for one
 *  currency keeps the lines that have a figure in it, or a caveat —
 *  "Margin (not posting yet)" is the whole point of the caveat and stays
 *  with no figure at all — so a currency's block is not every category of
 *  the desk with a dash; a section with no line says what is missing, in
 *  words, never a lone row of dashes under a heading; a total stands only
 *  where there is one.
 *
 *  21 Oct 2026 audit: a section of ONE line has no total row (it would be that
 *  line again — "Margin 2,410.25 / Total income 2,410.25"); the Profit row
 *  closes the matrix only — a phone block says its profit in its head (the
 *  currency's own header), once; an incomplete profit is neutral with the word
 *  beside it (`view`); the Margin line's "not posting yet" tag is dropped
 *  while the "margin reads zero" warning above says the same (a caveat with
 *  no such warning keeps its tag); and the "both desks" tag is gone with the
 *  second desk. */
function reportRows(data: PnlResponse, assets: string[], every: boolean, view: View): ReportRow[] {
  const stands = (l: PnlLine) => every || !!l.caveat || assets.some((a) => l.amounts[a] !== undefined);

  const figures = (amounts: PnlAmounts, opts: { tone?: 'plain' | 'signed'; strong?: boolean } = {}) =>
    Object.fromEntries(assets.map((a) => [a, <Figure key={a} value={amounts[a]} asset={a} {...opts} />]));

  const lineRow = (l: PnlLine): ReportRow => ({
    key: l.key,
    cells: {
      line: (
        <>
          <span>{lineLabel(l)}</span>
          {l.caveat && !(l.key === 'spread' && view.spreadSaid) && <span className="ml-2 text-meta text-warn">{l.caveat}</span>}
        </>
      ),
      ...figures(l.amounts),
    },
  });
  const emptyRow = (key: string, what: string): ReportRow => ({
    key, kind: 'group',
    cells: { line: <span className="font-normal text-meta text-tg-hint">Nothing {what} in this period.</span> },
  });

  /** A section: its heading, its lines, its total — or its heading and the
   *  sentence for an empty one. */
  const section = (s: PnlSection, id: string, title: string, totalLabel: string, what: string): ReportRow[] => {
    const lines = s.lines.filter(stands);
    const hasTotal = every || assets.some((a) => s.totals[a] !== undefined);
    return [
      { key: `g-${id}`, kind: 'group', cells: { line: title } },
      ...(lines.length === 0
        ? [emptyRow(`e-${id}`, what)]
        : [...lines.map(lineRow),
           // One line: its total would be itself again.
           ...(hasTotal && lines.length > 1 ? [{ key: `t-${id}`, kind: 'total' as const, cells: { line: totalLabel, ...figures(s.totals, { strong: true }) } }] : [])]),
    ];
  };

  const ownerLines = [data.owner.capital_in, data.owner.drawings, data.owner.other].flatMap((s) => s.lines).filter(stands);
  const ownerRows: ReportRow[] = ownerLines.length > 0
    ? [{ key: 'g-owner', kind: 'group', cells: { line: 'Owner · not part of profit' } }, ...ownerLines.map(lineRow)]
    : [];

  // A currency that neither earned nor spent this period, on the phone: its
  // head already reads "profit —", so the block says so once, in one line,
  // and goes straight to the owner lines — not two empty sections and a
  // dashed profit row, eight rows of nothing per idle coin (19 Sept 2026).
  // The matrix keeps the full shape: there the coin is one column among
  // many, and the rows are the report's.
  if (!every && !data.income.lines.some(stands) && !data.expenses.lines.some(stands)) {
    return [emptyRow('e-all', 'earned or spent'), ...ownerRows];
  }

  return [
    ...section(data.income,   'income',   'Income',   'Total income',   'earned'),
    ...section(data.expenses, 'expenses', 'Expenses', 'Total expenses', 'spent'),
    // The profit line. Income − expenses, and nothing else. On the phone the
    // block's head says it — said once.
    ...(every ? [{
      key: 'profit', kind: 'total' as const,
      cells: {
        line: <>Profit{view.incomplete && <span className="ml-2 text-meta font-normal text-warn">{view.word}</span>}</>,
        ...Object.fromEntries(assets.map((a) => [a,
          <ProfitFigure key={a} value={data.profit[a]} asset={a} incomplete={view.incomplete} className="amt-key text-body" />])),
      },
    }] : []),
    // Owner — under the profit line, in its own group, so it can never be
    // mistaken for part of the profit above it. Same columns, so the
    // currencies still line up and the eye can compare.
    ...ownerRows,
  ];
}

/* ════════════════ Desktop — the matrix ════════════════ */

/** THE ANSWER FIRST (21 Oct 2026 audit: on the console the profit was the last
 *  block, under seventeen rows — the phone leads with it). One line above the
 *  matrix: the profit of each currency, the very figures the phone's block
 *  heads read (`data.profit`; nothing is computed here), and — when the
 *  report says the profit is partial — the word. */
function ResultStrip({ data, assets, view }: { data: PnlResponse; assets: string[]; view: View }) {
  // A currency with only owner money has no profit figure to lead with; the matrix below still has its column.
  const stated = assets.filter((a) => data.profit[a] !== undefined);
  if (stated.length === 0) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-xl border border-stroke bg-panel px-4 py-3">
      <span className="text-label font-medium uppercase tracking-[0.06em] text-tg-hint">Profit</span>
      {stated.map((a) => (
        <span key={a} className="flex items-baseline gap-1.5">
          <span className="text-meta text-tg-hint">{displayAsset(a)}</span>
          <ProfitFigure value={data.profit[a]} asset={a} incomplete={view.incomplete} className="amt-key text-body" />
        </span>
      ))}
      {view.incomplete && <span className="text-meta text-warn">{view.word}</span>}
    </div>
  );
}

function Matrix({ data, assets, view }: { data: PnlResponse; assets: string[]; view: View }) {
  return (
    <ReportTable
      columns={[
        { key: 'line', label: '', align: 'left', width: '38%' },
        ...assets.map((a) => ({ key: a, label: displayAsset(a) })),
      ]}
      rows={reportRows(data, assets, true, view)}
    />
  );
}

/* ════════════════ Phone — one block per currency ════════════════ */

/** The currency block the phone reads: the Ledger's block header — the
 *  currency at the left, "profit" and the figure at the right, so the answer
 *  is on the first line of every block as the eye scrolls — then the same
 *  report rows as the matrix, two columns wide: the line and this currency's
 *  figure. Headless and unframed: the header line is the LedgerHead's, and
 *  the block stands flat under it as Trades and Money's do. The head takes
 *  the table's own cell inset (px-4) so the currency stands over the line
 *  words and the profit over the figures, on one x. The line column wraps
 *  rather than scrolls — a long line name is longer than a phone is wide once
 *  the figure has its seat (it carried a "both desks" tag once; that is gone,
 *  21 Oct 2026, with the second desk). */
function CurrencyBlock({ data, asset, view }: { data: PnlResponse; asset: string; view: View }) {
  return (
    <section className="pt-3 pb-1">
      <LedgerHead
        className="px-4"
        title={displayAsset(asset)}
        figure={<ProfitFigure value={data.profit[asset]} asset={asset} incomplete={view.incomplete} />}
        note={view.incomplete ? <><span className="text-warn">{view.word}</span> profit</> : 'profit'}
        noteFirst
      />
      <ReportTable
        head={false}
        frame={false}
        columns={[
          { key: 'line', label: '', align: 'left', nowrap: false },
          { key: asset, label: displayAsset(asset) },
        ]}
        rows={reportRows(data, [asset], false, view)}
      />
    </section>
  );
}
